import { hasNegationOrDoubt } from "../resolvers/enum-slot.js";
import { isFirstPersonPronoun } from "../resolvers/member-slot.js";
import { sameWord, wordsOf, type Word } from "../resolvers/vi-words.js";
import type { AiInterpretDecision, AiInterpretRejectReason } from "./confidence-policy.js";

/**
 * 🔴🔴 AI-COMMON-UNKNOWN (@UC0206 @UC0269 @UC0213 @UC0216 @UC0214) — LƯỚI CHỌN TRANG cho NĂM khuôn câu THƯỜNG GẶP mà Người phiên dịch bỏ cuộc.
 *
 * ## Vấn đề (đo, không đoán — LLM thật 10/10, gpt-5.4-nano, persona thật, n=3; `aiq-probe/results.csv` của điều phối)
 *  - hanh *"đã thu đủ chưa em"* ⇒ `unknown` 3/3 — READ-COLLECTIONS có trong top-8 (hạng 8);
 *  - quan · tuan *"tiền sân ai (phải) trả ai"* ⇒ `unknown` 6/6 — READ-DEBTS hạng 2;
 *  - linh *"ck cho ai vay"* ⇒ `unknown` 3/3 — GROUP-BANK-ACCOUNT hạng 5;
 *  - hanh *"moi phu huynh vao app"* ⇒ `unknown` 3/3 — INVITE-LINK-MANAGE hạng 3;
 *  - hanh *"giờ chị gửi thông báo cho các phụ huynh kiểu gì, họ chưa cài app"* ⇒ SHARE-TRANSPARENCY 3/3 (link XEM sổ — không đưa ai vào nhóm);
 *  - khach *"có người mời tôi vào đội bóng, vào kiểu gì"* ⇒ INVITE-ACCEPT 2/3, `unknown` 1/3.
 * Vá Sổ (thêm câu vào `llm.examples`) đo offline: MỖI câu làm mục 340–669 bản ghi eval (trang được thêm câu nằm trong top-8 của hàng trăm ca, và
 * mọi ví dụ đều vào prompt) ⇒ vượt trần 150 của đợt ⇒ lưới tất định, 0 byte prompt, 0 ca eval phải thu lại.
 *
 * ## Khuôn (MỌI chữ của câu phải thuộc khuôn — luật chung 1 của `AI_DETERMINISTIC_NETS`)
 *
 *   tiến độ    [quỹ …|đợt [thu] [này]|lớp/đội/nhóm [mình|này]] [đã] thu [được] {đủ|hết} [tiền] [quỹ …] chưa {đuôi}*          ⇒ READ-COLLECTIONS
 *   ai trả ai  [tiền <mục đích 1–4 chữ>] ai {phải|cần|nên|còn|đang|sẽ}* trả [lại] [tiền] [cho] ai [bao nhiêu] {đuôi}*  ⇒ READ-DEBTS
 *              ("ai nợ ai" Người phiên dịch đã chọn đúng — ngoài khuôn, không can thiệp thêm)
 *   ck cho ai  [NGƯỜI] [phải|cần|sẽ] {ck|chuyển khoản|chuyển tiền|chuyển|bank} [tiền] ( cho ai | [quỹ …] vào đâu ) {đuôi}*  ⇒ GROUP-BANK-ACCOUNT
 *   mời người  [giờ] [NGƯỜI] [muốn|cần|…]* ( mời [thêm] NHỮNG-NGƯỜI [vào app|nhóm|lớp|đội] [CÁCH] [CHƯA-CÓ-APP]
 *                                            | {gửi thông báo|báo|nhắn [tin]|…} [cho] NHỮNG-NGƯỜI [CÁCH] CHƯA-CÓ-APP ) {đuôi}*  ⇒ INVITE-LINK-MANAGE
 *   mời tôi    {có người|có ai|ai đó|người ta|bạn tôi…} mời {tôi|mình|em|tớ|tui} [vào] [nhóm|đội|lớp… <tên ≤3 chữ>] [vào|nhận|tham gia…] [CÁCH] {đuôi}*
 *                                                                                                                       ⇒ INVITE-ACCEPT
 *
 *  - Câu mời người KHÔNG có "mời" thì BẮT BUỘC nói người nhận chưa có app ("họ chưa cài app"): "gửi thông báo cho phụ huynh" trơn có thể là nhắc tiền
 *    hay sổ công khai — không đoán.
 *  - Chữ CHUNG không phải tên (luật (c) của LANE-BRIEF): khuôn "ai trả ai" loại mục đích có chữ "quỹ" — tiền QUỸ là fund-flow, không phải nợ giữa thành
 *    viên (§3.6) — và (review #1373 LOW-1) "lương" / "ngân hàng" / "vay": không phải khoản chung chia giữa thành viên; tên riêng / số tiền / phủ định
 *    (`hasNegationOrDoubt` trên phần chữ TỰ DO) ⇒ ngoài khuôn.
 *  - 🔴 review #1373 HIGH — "mời tôi" BẮT BUỘC có NGƯỜI MỜI tường minh đứng trước ("có người/ai đó/bạn tôi/có anh…"; "ai" trơn KHÔNG — "ai mời em vào
 *    nhóm" là hỏi người mời hay nhờ ai đi mời). Vắng người mời thì "mời con/cháu/em/mình vào nhóm" là LỆNH đi mời người khác ⇒ mở INVITE-ACCEPT là SAI
 *    CHIỀU ⇒ ngoài khuôn (để LLM). "em/con/cháu" chỉ là TÔI ở chỗ người-được-mời SAU người mời tường minh — `me()` của các khuôn khác chỉ nhận ngôi 1.
 *  - review #1373 LOW-2 — "chuyển tiền QUỸ cho ai" = BÀN GIAO quỹ (đổi thủ quỹ), không phải hỏi số tài khoản nhận ⇒ "cho ai" chỉ khi KHÔNG có "quỹ";
 *    "[quỹ] vào đâu" giữ (hỏi nơi nhận tiền).
 *  - 🔴 "ck cho ai vay": "vay" KHÔNG DẤU vừa là "vậy" (trợ từ) vừa là "vay" (cho vay — chính cặp `MONEY_WORDS_PLAIN` của M07, review #1105 M1) ⇒ khớp
 *    khuôn nhưng MƠ HỒ ⇒ HỎI A/B [số tài khoản nhận · ai nợ ai], không đoán. "vậy" có dấu hay "vào đâu" ⇒ không mơ hồ.
 *  - Phủ định / nghi vấn (luật chung 4): đây là câu HỎI, nên chữ hỏi ("chưa", "bao nhiêu", "kiểu gì") chỉ có chỗ ở đúng vị trí hỏi của khuôn; "chưa"
 *    trong CHƯA-CÓ-APP nói về NGƯỜI NHẬN, không phủ định lời đề nghị. Phủ định ở chỗ khác ("đừng mời…", "chưa thu đủ") ⇒ ngoài khuôn.
 *
 * ## Một việc (luật chung 2): CHỌN TRANG — không điền ô, không ghi.
 *  - LLM THẬT đã đọc câu (luật chung 3 — chặn TƯỜNG MINH `rejected`/`simulated`) và bỏ cuộc (`not_understood`/`low_confidence`) ⇒ MỞ trang của khuôn
 *    (hoặc HỎI A/B khi khuôn mơ hồ); LLM chọn một trang KHÁC ⇒ HỎI A/B [trang LLM, trang lưới] qua nhánh `ask_ab` sẵn có (khuôn #1372 MEDIUM-2 —
 *    không đè, không cơ chế A/B thứ hai); LLM đã chọn đúng trang (hay một vế của khuôn mơ hồ) hoặc đã kẹt A/B ⇒ KHÔNG làm gì.
 *  - Trang đích (và vế thứ hai của A/B) phải nằm trong `allowed` (cổng vai/cờ — @UC0152): thiếu ⇒ `null` (đường cũ), không hỏi nửa vời.
 * Bốn trang đích là trang ĐỌC hoặc redirect (0 phiếu ghi): ô trống ⇒ mặc định Sổ của trang đọc (READ-COLLECTIONS ⇒ "tiến độ"). Thuần, 0 LLM, 0 I/O.
 */
export type CommonQuestionKind = "collection_progress" | "who_owes_whom" | "transfer_to_whom" | "invite_people" | "invited_me";

export interface CommonQuestionMatch {
  readonly kind: CommonQuestionKind;
  /** Trang của khuôn. */
  readonly page: string;
  /** Khuôn MƠ HỒ ⇒ vế thứ hai của câu hỏi A/B. */
  readonly alt?: string;
}

const PAGE_OF: Readonly<Record<CommonQuestionKind, string>> = Object.freeze({
  collection_progress: "READ-COLLECTIONS",
  who_owes_whom: "READ-DEBTS",
  transfer_to_whom: "GROUP-BANK-ACCOUNT",
  invite_people: "INVITE-LINK-MANAGE",
  invited_me: "INVITE-ACCEPT",
});
/** Vế thứ hai khi "ck cho ai vay" mơ hồ — "cho ai vay" là nợ giữa thành viên (READ-DEBTS: "ứng, đóng hộ, cho mượn"). */
const LOAN_PAGE = "READ-DEBTS";
/** Mọi trang lưới có thể mở/hỏi — test kiểm chúng CÓ THẬT trong Sổ. */
export const COMMON_QUESTION_PAGES: readonly string[] = Object.freeze([...new Set([...Object.values(PAGE_OF), LOAN_PAGE])]);

type Phrase = readonly Word[];
const phrases = (...xs: string[]): readonly Phrase[] => xs.map((x) => wordsOf(x));

const TAIL = phrases("vậy", "thế", "nhỉ", "nhể", "ạ", "à", "hả", "ha", "ta", "ơi", "em", "anh", "chị", "nhé", "nha", "với", "bạn", "ad", "đây", "zậy");
const FUND = phrases("quỹ");
const FUND_KINDS = phrases("lớp", "đội", "nhóm", "chung", "câu lạc bộ", "clb", "hội");
const GROUP_NOUNS = phrases("lớp", "đội", "nhóm");
const GROUP_OWN = phrases("mình", "này", "ta");
const BATCH = phrases("đợt");
const THIS = phrases("này");
const DONE_PAST = phrases("đã");
const COLLECT = phrases("thu");
const GOT = phrases("được");
const ENOUGH = phrases("đủ", "hết");
const MONEY = phrases("tiền");
const NOT_YET = phrases("chưa");
const WHO = phrases("ai");
const OWE_MODS = phrases("phải", "cần", "nên", "còn", "đang", "sẽ");
const OWE_VERBS = phrases("trả");
const BACK = phrases("lại");
const TO = phrases("cho");
const HOW_MUCH = phrases("bao nhiêu", "bn");
const SPEAKERS = phrases("em", "anh", "chị", "con", "cháu", "cô");
const MUST = phrases("phải", "cần", "sẽ");
const TRANSFER = phrases("chuyển khoản", "chuyển tiền", "ck", "chuyển", "bank");
const WHERE_TO = phrases("vào đâu");
const TIME = phrases("bây giờ", "giờ");
const WANT = phrases("làm thế nào để", "làm sao để", "muốn", "cần", "định", "phải");
const INVITE = phrases("mời");
const MORE = phrases("thêm");
const QUANT = phrases("tất cả", "các", "mọi", "những", "mấy", "cả");
const PEOPLE = phrases("phụ huynh", "bố mẹ", "cha mẹ", "mọi người", "người", "thành viên", "bạn", "nhà", "ph", "ae", "anh em", "member", "mem");
const PEOPLE_MODS = phrases("trong nhóm", "trong lớp", "trong đội", "của lớp", "còn lại", "khác", "lớp", "nhóm");
const INTO = phrases("vào");
const APP = phrases("ứng dụng", "app");
const WHERE_IN = phrases("ứng dụng", "app", "nhóm", "lớp", "đội", "quỹ");
const HOW = phrases("như thế nào", "thế nào", "kiểu gì", "ra sao", "làm sao", "bằng cách nào", "cách nào", "sao", "được không", "đc không", "ở đâu");
const NOTIFY = phrases("gửi thông báo", "thông báo", "gửi tin nhắn", "gửi tin", "nhắn tin", "nhắn", "báo tin", "báo", "liên lạc");
const WHEN = phrases("khi", "mà", "nhưng");
const THEY = phrases("họ", "mọi người", "nhiều người", "các bạn", "mấy nhà", "nhiều nhà");
const STILL = phrases("vẫn", "còn", "đều");
const NO = phrases("chưa", "không", "ko", "chả");
const HAVE_APP = phrases("biết dùng", "cài", "tải", "có", "dùng", "xài", "vào");
/** Người mời TƯỜNG MINH — BẮT BUỘC ở khuôn "mời tôi" (review #1373 HIGH). Không có "ai" trơn. */
const INVITERS = phrases("có ai đó", "có người", "có ai", "ai đó", "người ta", "bạn tôi", "bạn mình", "có bạn", "có anh", "có chị");
/** "em/con/cháu" là TÔI CHỈ ở chỗ người-được-mời sau người mời tường minh (`invitee`). */
const ME_EXTRA = phrases("em", "con", "cháu");
/** Mục đích của "tiền <mục đích> ai trả ai" không được chứa: quỹ (fund-flow, §3.6) · lương · ngân hàng · vay (review #1373 LOW-1). */
const PURPOSE_STOPS = phrases("quỹ", "lương", "ngân hàng", "vay");
const JOIN = phrases("tham gia", "chấp nhận", "đồng ý", "join", "vào", "nhận");
const JOIN_OBJ = phrases("lời mời");
const JOIN_NOUNS = phrases("câu lạc bộ", "clb", "nhóm", "đội", "lớp", "hội", "quỹ", "group");
const THEN = phrases("thì", "giờ");
/** Chữ mà phần TÊN tự do ("đội <bóng>") không được chứa: chữ của chính khuôn phía sau (vào/nhận/cách hỏi) — tên dừng ở đó. */
const NAME_STOPS = [...JOIN, ...HOW, ...THEN, ...TAIL];

class Words {
  readonly unaccented: boolean;
  constructor(readonly w: readonly Word[]) {
    this.unaccented = w.every((x) => x.plain);
  }

  /** Độ dài cụm DÀI NHẤT trong `list` khớp tại `i`, 0 nếu không. */
  m(i: number, list: readonly Phrase[]): number {
    let best = 0;
    for (const p of list) if (p.length > best && i + p.length <= this.w.length && p.every((pw, k) => sameWord(this.w[i + k]!, pw))) best = p.length;
    return best;
  }

  /** `i` + cụm tuỳ chọn. */
  opt(i: number, list: readonly Phrase[]): number {
    return i + this.m(i, list);
  }

  /** `i` + mọi cụm lặp liền nhau của `list`. */
  many(i: number, list: readonly Phrase[]): number {
    let j = i;
    for (let n = this.m(j, list); n > 0; n = this.m(j, list)) j += n;
    return j;
  }

  /** `i` + cụm BẮT BUỘC, hoặc -1. */
  req(i: number, list: readonly Phrase[]): number {
    if (i < 0) return -1;
    const n = this.m(i, list);
    return n > 0 ? i + n : -1;
  }

  /** Từ `i` tới hết câu chỉ toàn trợ từ cuối. */
  endsAt(i: number): boolean {
    return i >= 0 && this.many(i, TAIL) === this.w.length;
  }

  /** "quỹ [lớp|đội|…]". */
  fund(i: number): number {
    const f = this.m(i, FUND);
    return f > 0 ? i + f + this.m(i + f, FUND_KINDS) : i;
  }

  /** Ngôi 1 rõ nghĩa (một nguồn: `isFirstPersonPronoun`); "minh" không dấu chỉ khi CẢ câu không dấu (lý lẽ ở M09 `isFundDuesQuestion`). */
  me(i: number): number {
    const x = this.w[i];
    if (x === undefined) return 0;
    return isFirstPersonPronoun(x.accented) || (this.unaccented && x.key === "minh") ? 1 : 0;
  }

  /** Người-được-mời là TÔI: ngôi 1 hoặc "em/con/cháu" — chỉ gọi SAU người mời tường minh (review #1373 HIGH). */
  invitee(i: number): number {
    return this.me(i) > 0 ? this.me(i) : this.m(i, ME_EXTRA);
  }

  text(a: number, b: number): string {
    return this.w.slice(a, b).map((x) => x.accented).join(" ");
  }
}

/** tiến độ: [chủ đề] [đã] thu [được] {đủ|hết} [tiền] [quỹ …] chưa {đuôi}*. */
function collectionProgress(s: Words): boolean {
  let i = 0;
  if (s.m(0, FUND) > 0) i = s.fund(0);
  else if (s.m(0, BATCH) > 0) i = s.opt(s.opt(1, COLLECT), THIS);
  else if (s.m(0, GROUP_NOUNS) > 0) i = s.opt(1, GROUP_OWN);
  i = s.req(s.opt(i, DONE_PAST), COLLECT);
  if (i < 0) return false;
  i = s.req(s.opt(i, GOT), ENOUGH);
  if (i < 0) return false;
  i = s.fund(s.opt(i, MONEY));
  return s.endsAt(s.req(i, NOT_YET));
}

/** ai trả ai: [tiền <mục đích>] ai {phải|…}* trả [lại] [tiền] [cho] ai [bao nhiêu] {đuôi}*. */
function whoOwesWhom(s: Words): boolean {
  let i = 0;
  if (s.m(0, MONEY) > 0) {
    let j = 1;
    while (j < s.w.length && s.m(j, WHO) === 0) j += 1;
    const purpose = s.text(1, j);
    // Mục đích 1–4 chữ, không chữ quỹ/lương/ngân hàng/vay (`PURPOSE_STOPS`), không số, không phủ định.
    if (j === 1 || j > 5 || j === s.w.length || /\d/u.test(purpose) || s.w.slice(1, j).some((_, k) => s.m(1 + k, PURPOSE_STOPS) > 0) || hasNegationOrDoubt(purpose)) return false;
    i = j;
  }
  i = s.req(i, WHO);
  if (i < 0) return false;
  i = s.req(s.many(i, OWE_MODS), OWE_VERBS);
  if (i < 0) return false;
  i = s.opt(s.opt(s.opt(i, BACK), MONEY), TO);
  i = s.req(i, WHO);
  return i >= 0 && s.endsAt(s.opt(i, HOW_MUCH));
}

/** ck cho ai: [NGƯỜI] [phải|cần|sẽ] CHUYỂN [tiền] ( cho ai | [quỹ …] vào đâu ) {đuôi}* ⇒ trang + (mơ hồ) trang thứ hai. */
function transferToWhom(s: Words): CommonQuestionMatch | null {
  let i = s.me(0) > 0 ? s.me(0) : s.m(0, SPEAKERS);
  i = s.req(s.opt(i, MUST), TRANSFER);
  if (i < 0) return null;
  i = s.opt(i, MONEY);
  const afterFund = s.fund(i);
  // "chuyển tiền QUỸ cho ai" = bàn giao quỹ (review #1373 LOW-2) ⇒ "cho ai" chỉ khi không có "quỹ".
  const toWho = afterFund === i && s.m(i, TO) > 0 && s.m(i + 1, WHO) > 0;
  i = afterFund;
  const target = toWho ? i + 2 : s.req(i, WHERE_TO);
  if (target < 0 || !s.endsAt(target)) return null;
  // "vay" KHÔNG dấu sau "cho ai" ⇒ "vậy" hay "vay" (cho vay) — không phân biệt được bằng chữ ⇒ hỏi.
  const loanOrParticle = toWho && s.w.slice(target).some((x) => x.plain && x.key === "vay");
  return { kind: "transfer_to_whom", page: PAGE_OF.transfer_to_whom, ...(loanOrParticle ? { alt: LOAN_PAGE } : {}) };
}

/** NHỮNG-NGƯỜI: [các|mọi|…] {phụ huynh|người|…} [trong nhóm|khác|…]. */
function people(s: Words, i: number): number {
  const j = s.req(s.opt(i, QUANT), PEOPLE);
  return j < 0 ? -1 : s.opt(j, PEOPLE_MODS);
}

/** CHƯA-CÓ-APP: [khi|mà] [họ|NHỮNG-NGƯỜI] {vẫn|còn}* {chưa|không} {cài|tải|có|…} [được] app. */
function noApp(s: Words, i: number): number {
  let j = s.opt(i, WHEN);
  if (s.m(j, THEY) > 0) j = s.opt(j, THEY);
  else if (people(s, j) > 0) j = people(s, j);
  j = s.req(s.many(j, STILL), NO);
  j = s.req(j, HAVE_APP);
  return j < 0 ? -1 : s.req(s.opt(j, GOT), APP);
}

/** mời người: [giờ] [NGƯỜI] [muốn…]* ( mời … | báo/nhắn … CHƯA-CÓ-APP ) {đuôi}*. */
function invitePeople(s: Words): boolean {
  let i = s.opt(0, TIME);
  i += s.me(i) > 0 ? s.me(i) : s.m(i, SPEAKERS);
  i = s.many(i, WANT);
  if (s.m(i, INVITE) > 0) {
    let j = people(s, s.opt(i + 1, MORE));
    if (j < 0) return false;
    if (s.m(j, INTO) > 0) j = s.req(j + 1, WHERE_IN);
    if (j < 0) return false;
    j = s.opt(j, HOW);
    const tail = noApp(s, j);
    return s.endsAt(tail >= 0 ? tail : j);
  }
  let j = s.req(i, NOTIFY);
  if (j < 0) return false;
  j = people(s, s.opt(j, TO));
  if (j < 0) return false;
  return s.endsAt(noApp(s, s.opt(j, HOW)));
}

/** mời tôi: AI-MỜI (BẮT BUỘC) mời TÔI [vào] [nhóm <tên ≤3>] [thì|giờ] [TÔI] [vào|nhận…] [lời mời] [CÁCH] {đuôi}*. */
function invitedMe(s: Words): boolean {
  let i = s.req(s.req(0, INVITERS), INVITE);
  if (i < 0 || s.invitee(i) === 0) return false;
  i = s.opt(i + s.invitee(i), INTO);
  const noun = s.m(i, JOIN_NOUNS);
  if (noun > 0) {
    let j = i + noun;
    while (j < s.w.length && j - (i + noun) < 3 && s.m(j, NAME_STOPS) === 0) j += 1;
    if (hasNegationOrDoubt(s.text(i + noun, j))) return false;
    i = j;
  }
  i = s.opt(i, THEN);
  i += s.invitee(i);
  if (s.m(i, JOIN) > 0) i = s.opt(s.opt(i, JOIN), JOIN_OBJ);
  return s.endsAt(s.opt(i, HOW));
}

/** Câu thuộc khuôn nào (và trang của khuôn), hoặc `null`. Thuần — chỉ đọc chữ của câu. */
export function parseCommonQuestion(sentence: string): CommonQuestionMatch | null {
  const w = wordsOf(sentence);
  if (w.length === 0) return null;
  const s = new Words(w);
  if (collectionProgress(s)) return { kind: "collection_progress", page: PAGE_OF.collection_progress };
  if (whoOwesWhom(s)) return { kind: "who_owes_whom", page: PAGE_OF.who_owes_whom };
  const transfer = transferToWhom(s);
  if (transfer !== null) return transfer;
  if (invitePeople(s)) return { kind: "invite_people", page: PAGE_OF.invite_people };
  if (invitedMe(s)) return { kind: "invited_me", page: PAGE_OF.invited_me };
  return null;
}

export interface CommonQuestionNetInput {
  readonly sentence: string;
  /** Trang người này mở được NGAY BÂY GIỜ (cổng vai/cờ — @UC0152). */
  readonly allowed: readonly { readonly id: string }[];
  /** Quyết định của chính sách trên Phiếu THẬT (`decideInterpretation`). */
  readonly decision: AiInterpretDecision;
  readonly rejected: AiInterpretRejectReason | undefined;
  readonly simulated: boolean;
}

/**
 * LƯỚI CHỌN TRANG (kind `page`) — `run` trang của khuôn khi LLM bỏ cuộc; `ask_ab` khi khuôn mơ hồ hoặc LLM chọn trang khác; `null` = đường cũ.
 * Luật đầy đủ ở docblock đầu file.
 */
export function commonQuestionPageFromSentence(input: CommonQuestionNetInput): AiInterpretDecision | null {
  if (input.rejected !== undefined || input.simulated) return null;
  const { decision } = input;
  if (decision.action === "ask_ab") return null;
  if (decision.action === "menu" && decision.reason !== "not_understood" && decision.reason !== "low_confidence") return null;
  const match = parseCommonQuestion(input.sentence);
  if (match === null) return null;
  const allowed = (id: string): boolean => input.allowed.some((p) => p.id === id);
  if (!allowed(match.page) || (match.alt !== undefined && !allowed(match.alt))) return null;
  if (decision.action === "run") {
    if (decision.page === match.page || decision.page === match.alt) return null;
    return { action: "ask_ab", pages: [decision.page, match.page] };
  }
  return match.alt !== undefined ? { action: "ask_ab", pages: [match.page, match.alt] } : { action: "run", page: match.page };
}
